
import React, { useEffect, useState } from "react";
import axios from 'axios';
import {
    Button,
    Card,
    Col,
    Modal,
    Row,
    Space,
    Tag,
    Typography,
} from "antd";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ReloadOutlined,
    WarningFilled,
    CloudServerOutlined,
} from "@ant-design/icons";
import { BackendAPI } from "../../axios";

const { Text } = Typography;

const THEME_COLOR = "#1515b4";

type ServerStatusType = "UP" | "HIGH_LOAD" | "UNAVAILABLE";

interface ServerStatusRow {
    service: string;
    Habbel: ServerStatusType;
    Manesar: ServerStatusType;
}

interface BackendServerStatus {
    appName: string;
    dc: string;
    status: string;
}

interface ServerStatusProps {
    visible: boolean;
    onClose: () => void;
}

const ServerStatus: React.FC<ServerStatusProps> = ({
    visible,
    onClose,
}) => {
    const [loading, setLoading] = useState(false);

    const [serverStatus, setServerStatus] = useState<
        ServerStatusRow[]
    >([]);

    /**
     * Convert backend status code into UI status
     *
     * 0  -> UP
     * 1  -> HIGH_LOAD
     * NA -> UNAVAILABLE
     * Other -> UNAVAILABLE
     */
    const getStatusType = (status: string): ServerStatusType => {
        if (status === "0") {
            return "UP";
        }

        if (status === "1") {
            return "HIGH_LOAD";
        }

        return "UNAVAILABLE";
    };

    /**
     * Convert backend response into UI format
     *
     * hdc -> Habbel
     * mdc -> Manesar
     */
    const transformResponse = (
        response: BackendServerStatus[]
    ): ServerStatusRow[] => {
        const services = ["auth", "otp", "kyc", "facerd"];

        return services.map((service) => {
            const habbelData = response.find(
                (item) =>
                    item.appName.toLowerCase() === service &&
                    item.dc.toLowerCase() === "hdc"
            );

            const manesarData = response.find(
                (item) =>
                    item.appName.toLowerCase() === service &&
                    item.dc.toLowerCase() === "mdc"
            );

            return {
                service: service.toUpperCase(),

                Habbel: getStatusType(
                    habbelData?.status ?? "NA"
                ),

                Manesar: getStatusType(
                    manesarData?.status ?? "NA"
                ),
            };
        });
    };

    /**
     * Get server status from backend
     */

    const getRefreshToken = async () => {
  try {
    const response = await BackendAPI.get("/user/refresh-token", {
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
   // console.error(error);
    if (axios.isAxiosError(error)) {
        if (error.request.status == 401) {
          window.location.reload();
        }
      }
  }
};
    const fetchServerStatus = async () => {
        try {
            setLoading(true);
            const bearerToken= await getRefreshToken();
            console.log(bearerToken);
            const response = await BackendAPI.get(
                "/server/status",
                {
                     headers: {
                Authorization: `Bearer ${bearerToken}`,
            },
                }

            );

            console.log(
                "Server Status API Response:",
                response.data
            );

            if (Array.isArray(response.data)) {
                const formattedData = transformResponse(
                    response.data
                );

                setServerStatus(formattedData);
            } else {
                setServerStatus([]);
            }
        } catch (error) {
            console.error(
                "Failed to fetch server status:",
                error
            );

            /*
             * If backend API itself is unavailable,
             * show all services as unavailable.
             */
            const services = [
                "AUTH",
                "OTP",
                "KYC",
                "FACERD",
            ];

            setServerStatus(
                services.map((service) => ({
                    service,
                    Habbel: "UNAVAILABLE",
                    Manesar: "UNAVAILABLE",
                }))
            );
        } finally {
            setLoading(false);
        }
    };

    /**
     * Call API whenever modal is opened
     */
    useEffect(() => {
        if (visible) {
            fetchServerStatus();
        }
    }, [visible]);

    const getStatusDetails = (
        status: ServerStatusType
    ) => {
        switch (status) {
            case "UP":
                return {
                    text: "Up & Stable",
                    color: "success",
                    icon: <CheckCircleFilled />,
                };

            case "HIGH_LOAD":
                return {
                    text: "High Load",
                    color: "warning",
                    icon: <WarningFilled />,
                };

            case "UNAVAILABLE":
                return {
                    text: "Health Status Not Available",
                    color: "default",
                    icon: <CloseCircleFilled />,
                };

            default:
                return {
                    text: "Health Status Not Available",
                    color: "default",
                    icon: <CloseCircleFilled />,
                };
        }
    };

    const getOverallStatus = () => {
        const statuses = serverStatus.flatMap((item) => [
            item.Habbel,
            item.Manesar,
        ]);

        /*
         * If any service is unavailable,
         * show health status as unavailable.
         */
        if (statuses.includes("UNAVAILABLE")) {
            return {
                text: "Health Status Not Available",
                color: "#595959",
                background: "#fafafa",
                border: "#d9d9d9",
                icon: <CloseCircleFilled />,
            };
        }

        /*
         * If any service is under high load
         */
        if (statuses.includes("HIGH_LOAD")) {
            return {
                text: "Some Services Under High Load",
                color: "#ad6800",
                background: "#fffbe6",
                border: "#ffe58f",
                icon: <WarningFilled />,
            };
        }

        /*
         * All services are UP
         */
        return {
            text: "All Services Operational",
            color: "#237804",
            background: "#f6ffed",
            border: "#b7eb8f",
            icon: <CheckCircleFilled />,
        };
    };

    const overallStatus = getOverallStatus();

    const handleRefresh = async () => {
        await fetchServerStatus();
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width={900}
            title={
                <Space size={8}>
                    <CloudServerOutlined
                        style={{
                            color: THEME_COLOR,
                            fontSize: 19,
                        }}
                    />

                    <span
                        style={{
                            color: THEME_COLOR,
                            fontSize: 18,
                            fontWeight: 600,
                        }}
                    >
                        UIDAI Server Status
                    </span>
                </Space>
            }
            styles={{
                body: {
                    padding: "4px 2px 0",
                },
            }}
        >
            {/* Overall Status */}
            <div
                style={{
                    background: overallStatus.background,
                    border: `1px solid ${overallStatus.border}`,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Space size={10}>
                    <span
                        style={{
                            color: overallStatus.color,
                            fontSize: 20,
                        }}
                    >
                        {overallStatus.icon}
                    </span>

                    <div>
                        <Text
                            strong
                            style={{
                                display: "block",
                                fontSize: 13,
                                color: "#434343",
                            }}
                        >
                            Overall System Status
                        </Text>

                        <Text
                            style={{
                                fontSize: 12,
                                color: overallStatus.color,
                                fontWeight: 600,
                            }}
                        >
                            {loading
                                ? "Checking server status..."
                                : overallStatus.text}
                        </Text>
                    </div>
                </Space>

                <Tag
                    color="blue"
                    style={{
                        margin: 0,
                        borderRadius: 12,
                        fontSize: 11,
                    }}
                >
                    Live Status
                </Tag>
            </div>

            {/* Server Information */}
            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col xs={24} md={12}>
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: "8px",
                            boxShadow:
                                "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                        styles={{
                            body: {
                                padding: "11px 14px",
                            },
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div>
                                <Text
                                    strong
                                    style={{
                                        color: THEME_COLOR,
                                        fontSize: 14,
                                    }}
                                >
                                    Habbel Server
                                </Text>

                                <div
                                    style={{
                                        marginTop: 2,
                                    }}
                                >
                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize: 11,
                                        }}
                                    >
                                        Habbel instance
                                    </Text>
                                </div>
                            </div>

                            <Tag
                                color="success"
                                style={{
                                    margin: 0,
                                    borderRadius: 12,
                                    fontSize: 11,
                                }}
                            >
                                <CheckCircleFilled /> Online
                            </Tag>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: "8px",
                            boxShadow:
                                "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                        styles={{
                            body: {
                                padding: "11px 14px",
                            },
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div>
                                <Text
                                    strong
                                    style={{
                                        color: THEME_COLOR,
                                        fontSize: 14,
                                    }}
                                >
                                    Manesar Server
                                </Text>

                                <div
                                    style={{
                                        marginTop: 2,
                                    }}
                                >
                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize: 11,
                                        }}
                                    >
                                        Manesar instance
                                    </Text>
                                </div>
                            </div>

                            <Tag
                                color="success"
                                style={{
                                    margin: 0,
                                    borderRadius: 12,
                                    fontSize: 11,
                                }}
                            >
                                <CheckCircleFilled /> Online
                            </Tag>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Service Status */}
            <Card
                bordered={false}
                title={
                    <span
                        style={{
                            color: THEME_COLOR,
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        Service Status
                    </span>
                }
                extra={
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 11,
                        }}
                    >
                        Habbel / Manesar
                    </Text>
                }
                style={{
                    borderRadius: "8px",
                    boxShadow:
                        "0 2px 8px rgba(0,0,0,0.08)",
                }}
                styles={{
                    header: {
                        minHeight: 40,
                        padding: "0 14px",
                    },
                    body: {
                        padding: 0,
                    },
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "1.2fr 1fr 1fr",
                        alignItems: "center",
                        background: "#fafafa",
                        borderTop:
                            "1px solid #f0f0f0",
                        borderBottom:
                            "1px solid #f0f0f0",
                        padding: "7px 14px",
                    }}
                >
                    <Text
                        strong
                        style={{
                            fontSize: 12,
                            color: "#595959",
                        }}
                    >
                        Service
                    </Text>

                    <Text
                        strong
                        style={{
                            fontSize: 12,
                            color: "#595959",
                        }}
                    >
                        Habbel
                    </Text>

                    <Text
                        strong
                        style={{
                            fontSize: 12,
                            color: "#595959",
                        }}
                    >
                        Manesar
                    </Text>
                </div>

                {/* Services */}
                {serverStatus.map((item, index) => {
                    const Habbel = getStatusDetails(
                        item.Habbel
                    );

                    const Manesar = getStatusDetails(
                        item.Manesar
                    );

                    return (
                        <div
                            key={item.service}
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "1.2fr 1fr 1fr",
                                alignItems: "center",
                                minHeight: 46,
                                padding: "5px 14px",
                                borderBottom:
                                    index ===
                                    serverStatus.length - 1
                                        ? "none"
                                        : "1px solid #f0f0f0",
                            }}
                        >
                            <Text
                                strong
                                style={{
                                    fontSize: 13,
                                    color: "#262626",
                                }}
                            >
                                {item.service}
                            </Text>

                            <Tag
                                color={Habbel.color}
                                icon={Habbel.icon}
                                style={{
                                    margin: 0,
                                    width: "fit-content",
                                    borderRadius: 12,
                                    fontSize: 11,
                                }}
                            >
                                {Habbel.text}
                            </Tag>

                            <Tag
                                color={Manesar.color}
                                icon={Manesar.icon}
                                style={{
                                    margin: 0,
                                    width: "fit-content",
                                    borderRadius: 12,
                                    fontSize: 11,
                                }}
                            >
                                {Manesar.text}
                            </Tag>
                        </div>
                    );
                })}
            </Card>

            {/* Footer */}
            <div
                style={{
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Text
                    type="secondary"
                    style={{
                        fontSize: 11,
                    }}
                >
                    Status fetched from UIDAI Servers
                </Text>

                <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
            </div>
        </Modal>
    );
};

export default ServerStatus;
